import { DisplayDateRange } from 'components/Charting/types';
import { DYNATRACE_ACTIONS } from 'constants/dynatrace-actions';
import { format } from 'date-fns';
import {
  AllTradeMarkets,
  MarketKey,
} from 'pages/Optimisation/components/ContractedVolume/types';
import {
  CHART_CONSTANTS,
  MARKET_FILTER_VALUES,
} from 'pages/Optimisation/constants';
import { IChartTrade, TRADE_TYPE, TradeSideType } from 'types/trade';
import { downloadFile } from 'utils/download-file';
import { dynatraceTrackInstantAction } from 'utils/dynatrace';
import { getExcelExport } from 'utils/excel';
import { roundToDecimal, roundToDecimalFixedString } from 'utils/number';
import {
  endOfTradingDay,
  getContractedPeriodTitle,
  startOfTradingDay,
} from 'utils/optimisation';

interface OptimisationExcelExportRow {
  Site: string;
  'Start Date': string;
  'End Date': string;
  'Period Start': string;
  'Period End': string;
  'Trade ID': string;
  'Trade Start Time': string; // 9:00
  'Trade End Time': string; // 21:00
  'UTC Trade Start': string; // UTC timestamp
  'UTC Trade End': string; // UTC timestamp
  Market: string;
  'Buy/Sell': 'Buy' | 'Sell';
  'Price (£/MWh)': string; // £/MWh
  'Power (MW)': string; // MW
  Hours: string;
  'Total (£)': string; // price * power * hours
}

const labelsByMarketAndIdentifier = CHART_CONSTANTS.legendLabels[
  MARKET_FILTER_VALUES.All
].reduce(
  (acc, { market, identifier, label, exportLabel }) =>
    acc.set(
      market,
      acc.get(market)?.set(identifier, exportLabel ?? label) ??
        new Map([[identifier, exportLabel ?? label]]),
    ),
  new Map<string, Map<string, string>>(),
);

const getFormattedBuyOrSell = (tradeSide: TradeSideType) =>
  tradeSide === 'buy'
    ? 'Buy'
    : tradeSide === 'sell'
      ? 'Sell'
      : (undefined as never);

/* istanbul ignore next: covered by snapshot @preserve */
export const getExcelExportRows = (
  data: IChartTrade[],
  site: string,
): OptimisationExcelExportRow[] => {
  const sortedTrades = data.sort((a, b) => +a.start - +b.start);

  const rows: OptimisationExcelExportRow[] = sortedTrades.map((trade) => {
    const { start, end, key, market, side, price, value, tradeGroupSourceId } =
      trade;
    const startOfDayOfStartDate = startOfTradingDay(start);
    const endOfDayOfStartDate = endOfTradingDay(start);

    const hours = roundToDecimal((+end - +start) / (1000 * 60 * 60), 6);

    const volume = Math.abs(value);

    const negative = (() => {
      if (key === MarketKey.Ancillary) {
        // Ancillary trades always are priced according to the price
        // customer will be paid for a positively priced trade, regardless if import/export
        // customer will pay for a negatively priced trade, regardless if import/export
        return 1;
      }

      return side === 'buy' ? -1 : 1;
    })();

    const total = roundToDecimal(price * volume * hours * negative, 6);

    const periodOne = getContractedPeriodTitle(
      key,
      start,
      startOfDayOfStartDate,
      endOfDayOfStartDate,
      market as AllTradeMarkets,
    );
    const periodTwo = getContractedPeriodTitle(
      key,
      end,
      startOfDayOfStartDate,
      endOfDayOfStartDate,
      market as AllTradeMarkets,
    );
    const row: OptimisationExcelExportRow = {
      Site: site,
      'Start Date': format(start, 'dd/MM/yyyy'),
      'End Date': format(end, 'dd/MM/yyyy'),
      'Period Start': periodOne,
      'Period End': periodTwo,
      'Trade ID': tradeGroupSourceId.toString(),
      'Trade Start Time': format(start, 'HH:mm'),
      'Trade End Time': format(end, 'HH:mm'),
      'UTC Trade Start': start.toISOString(),
      'UTC Trade End': end.toISOString(),

      Market: labelsByMarketAndIdentifier.get(key as MarketKey)!.get(market)!,
      'Buy/Sell': getFormattedBuyOrSell(side),
      'Price (£/MWh)': roundToDecimalFixedString(price, 2),
      'Power (MW)': roundToDecimalFixedString(volume, 2),
      Hours: roundToDecimalFixedString(hours, 3),
      'Total (£)': roundToDecimalFixedString(total, 2),
    };

    return row;
  });

  return rows;
};

const getDateDisplay = (date: Date): string => {
  const month = (date.getMonth() + 1).toString();
  const day = date.getDate().toString();
  const year = date.getFullYear().toString();
  return `${year}${month.length < 2 ? '0' : ''}${month}${
    day.length < 2 ? '0' : ''
  }${day}`;
};

export const getExcelExportFileName = (
  data: IChartTrade[],
  dateRange: DisplayDateRange,
  tradeTypes: TRADE_TYPE[],
): string => {
  if (!data.length) {
    throw new Error('No data');
  }

  const fromDateDisplay = getDateDisplay(new Date(dateRange.start));
  const toDateDisplay = getDateDisplay(new Date(dateRange.end));

  const typesDisplay = tradeTypes
    .map((type) => {
      switch (type) {
        case TRADE_TYPE.Ancillary:
          return 'Ancillary';
        case TRADE_TYPE.Wholesale:
          return 'Wholesale';
        case TRADE_TYPE.BalancingMechanism:
          return 'BM';
        default:
          return 'Unknown';
      }
    })
    .join('_');

  return `Optimisation_${fromDateDisplay}_${toDateDisplay}_${typesDisplay}.csv`;
};

export const getExcelExportBlob = async (data: IChartTrade[], site: string) =>
  getExcelExport([
    {
      title: 'Optimisation',
      rows: getExcelExportRows(data, site),
      columnOptions: {
        'Price (£/MWh)': { numFmt: '0.00' },
        'Power (MW)': { numFmt: '0.00' },
        Hours: { numFmt: '0.000' },
        'Total (£)': { numFmt: '0.00' },
      },
    },
  ]);

/* istanbul ignore next: requires browser @preserve */
export const downloadExcelExport = async (
  data: IChartTrade[],
  dateRange: DisplayDateRange,
  tradeTypes: TRADE_TYPE[],
  site: string,
) => {
  if (data.length) {
    dynatraceTrackInstantAction(DYNATRACE_ACTIONS.DOWNLOAD_TRADE_DATA);
    downloadFile(
      await getExcelExportBlob(data, site),
      getExcelExportFileName(data, dateRange, tradeTypes),
    );
  }
};
